import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { API } from "../../App";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  LayoutDashboard, Building2, Calendar, CalendarDays, Users, Tag, Layers,
  Home, FileText, CreditCard, Star, Shield, Search, Edit, Languages, Inbox, MessageSquare, Wrench, Sparkles, Database, FileUp, Layout,
  TrendingUp, Trash2, BarChart3, Link2, ListChecks,
  KeyRound, Globe, AlertTriangle, Mail,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import AdminTaxonomy from "./AdminTaxonomy";
import AdminCmsPages from "./AdminCmsPages";
import AdminBilling from "./AdminBilling";
import AdminFooterLinks from "./AdminFooterLinks";
import AdminCollections from "./AdminCollections";
import AdminVenues from "./AdminVenues";
import AdminUsers from "./AdminUsers";
import AdminVenueEdit from "./AdminVenueEdit";
import AdminVenuePlans from "./AdminVenuePlans";
import AdminVenueClaims from "./AdminVenueClaims";
import AdminCsvImport from "./AdminCsvImport";
import AdminLayouts from "./AdminLayouts";
import AdminTranslations from "./AdminTranslations";
import AdminHomepage from "./AdminHomepage";
import AdminInquiries from "./AdminInquiries";
import AdminMessages from "./AdminMessages";
import AdminMaintenance from "./AdminMaintenance";
import AdminBranding from "./AdminBranding";
import AdminDataTools from "./AdminDataTools";
import AdminTagGroups from "./AdminTagGroups";
import AdminReviews from "./AdminReviews";
import BookingStatusBadge from "../../components/BookingStatusBadge";
import BookingDateFilter, { getDateRange } from "../../components/BookingDateFilter";
import { getLocalized } from "../../utils/localized";
import { useFormatDate } from "../../utils/dateFormat";

export default function AdminDashboard() {
  const location = useLocation();
  const { t } = useTranslation();

  const navItems = [
    { path: "/admin", label: t("admin:overview", { defaultValue: "Overview" }), icon: LayoutDashboard },
    { path: "/admin/venues", label: t("admin:venues"), icon: Building2 },
    { path: "/admin/bookings", label: t("admin:bookings"), icon: Calendar },
    { path: "/admin/events", label: t("nav:events"), icon: CalendarDays },
    { path: "/admin/users", label: t("admin:users"), icon: Users },
    { path: "/admin/taxonomy", label: t("admin:taxonomy"), icon: Tag },
    { path: "/admin/tag-groups", label: t("admin:tag_groups", { defaultValue: "Tag Groups" }), icon: Layers },
    { path: "/admin/homepage", label: "Homepage", icon: Home },
    { path: "/admin/pages", label: t("admin:cms"), icon: FileText },
    { path: "/admin/translations", label: t("admin:translations"), icon: Languages },
    { path: "/admin/collections", label: t("admin:collections"), icon: Star },
    { path: "/admin/footer-links", label: t("admin:footer_links"), icon: Link2 },
    { path: "/admin/reviews", label: t("venue:reviews"), icon: Star },
    { path: "/admin/billing", label: t("admin:billing_plans"), icon: CreditCard },
    { path: "/admin/venue-plans", label: t("admin:venue_plans", { defaultValue: "Venue Plans" }), icon: Layers },
    { path: "/admin/venue-claims", label: t("admin:venue_claims", { defaultValue: "Venue Claims" }), icon: Inbox },
    { path: "/admin/csv", label: t("admin:csv", { defaultValue: "Bulk CSV" }), icon: FileUp },
    { path: "/admin/layouts", label: t("admin:layouts", { defaultValue: "Layouts" }), icon: Layout },
    { path: "/admin/inquiries", label: t("admin:inquiries", { defaultValue: "Inquiries" }), icon: Inbox },
    { path: "/admin/messages", label: t("admin:booking_messages", { defaultValue: "Booking Messages" }), icon: MessageSquare },
    { path: "/admin/maintenance", label: t("admin:maintenance", { defaultValue: "Maintenance" }), icon: Wrench },
    { path: "/admin/branding", label: t("admin:branding", { defaultValue: "Branding" }), icon: Sparkles },
    { path: "/admin/data-tools", label: t("admin:data_tools", { defaultValue: "Data Migration" }), icon: Database },
    { path: "/admin/permissions", label: t("admin:settings"), icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-muted/30">
      <Header />
      <div className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-56 shrink-0">
              <div className="bg-card rounded-xl border border-border p-3 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <Shield className="h-4 w-4 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground text-sm">{t("admin:admin_dashboard", { defaultValue: "Admin Dashboard" })}</p>
                  </div>
                </div>
              </div>
              <nav className="space-y-0.5">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                      data-testid={`admin-nav-${item.label.toLowerCase()}`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </aside>

            <main className="flex-1 min-w-0">
              <Routes>
                <Route index element={<OverviewPage />} />
                {/* Legacy /admin/analytics now redirects to merged Dashboard */}
                <Route path="analytics" element={<Navigate to="/admin" replace />} />
                <Route path="venues" element={<AdminVenues />} />
                <Route path="venues/:venueId/edit" element={<AdminVenueEdit />} />
                <Route path="bookings" element={<BookingsPage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="taxonomy" element={<AdminTaxonomy />} />
                <Route path="tag-groups" element={<AdminTagGroups />} />
                <Route path="homepage" element={<AdminHomepage />} />
                <Route path="pages" element={<AdminCmsPages />} />
                <Route path="translations" element={<AdminTranslations />} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="venue-plans" element={<AdminVenuePlans />} />
                <Route path="venue-claims" element={<AdminVenueClaims />} />
                <Route path="csv" element={<AdminCsvImport />} />
                <Route path="layouts" element={<AdminLayouts />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="maintenance" element={<AdminMaintenance />} />
                <Route path="branding" element={<AdminBranding />} />
                <Route path="data-tools" element={<AdminDataTools />} />
                <Route path="footer-links" element={<AdminFooterLinks />} />
                <Route path="collections" element={<AdminCollections />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="permissions" element={<PermissionsPage />} />
              </Routes>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [statsRes, overviewRes, trendRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/admin/analytics/overview`),
          axios.get(`${API}/admin/analytics/bookings-trend?days=30`),
        ]);
        setStats(statsRes.data);
        setAnalytics(overviewRes.data);
        setTrend(trendRes.data);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-28 rounded-xl" />)}
      </div>
    );
  }

  const maxBookings = Math.max(...(trend?.map(d => d.bookings) || [1]), 1);

  return (
    <div data-testid="admin-overview-page">
      <h1 className="text-2xl font-medium text-foreground mb-6 flex items-center gap-2">
        <LayoutDashboard className="h-6 w-6 text-primary" />
        {t("admin:overview", { defaultValue: "Overview" })}
      </h1>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <StatCard title={t("admin_dashboard:total_venues")} value={stats?.total_venues || 0} icon={Building2} />
        <StatCard title={t("admin_dashboard:total_bookings")} value={stats?.total_bookings || 0} icon={Calendar} />
        <StatCard title={t("admin_dashboard:todays_bookings")} value={stats?.bookings_today || 0} icon={CalendarDays} />
        <StatCard title={t("admin_dashboard:pending")} value={stats?.pending_bookings || 0} icon={ListChecks} />
        <StatCard title={t("admin_dashboard:total_users")} value={stats?.total_users || 0} icon={Users} />
        <StatCard title={t("admin_dashboard:total_events")} value={stats?.total_events || 0} icon={CalendarDays} />
      </div>

      {/* Analytics charts */}
      {analytics && (
        <>
          <h2 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            {t("admin_dashboard:analytics_section")}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Bookings Trend */}
            <div className="lg:col-span-2 bg-card rounded-xl border border-border p-6">
              <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" /> {t("admin_dashboard:booking_trend_30d")}
              </h3>
              <div className="flex items-end gap-[2px] h-40" data-testid="bookings-trend-chart">
                {trend.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center group relative">
                    <div
                      className="w-full bg-primary/20 hover:bg-primary/40 rounded-t transition-colors cursor-pointer"
                      style={{ height: `${Math.max((d.bookings / maxBookings) * 100, 2)}%` }}
                      title={`${d.date}: ${d.bookings} · ${d.guests} ${t("common:guests")}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                <span>{trend[0]?.date}</span>
                <span>{trend[trend.length - 1]?.date}</span>
              </div>
            </div>

            {/* Status Breakdown */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h3 className="font-medium text-foreground mb-4">{t("admin_dashboard:booking_status")}</h3>
              <div className="space-y-3">
                {Object.entries(analytics.status_breakdown || {}).map(([status, count]) => {
                  const total = analytics.totals?.bookings || 1;
                  const pct = Math.round((count / total) * 100);
                  const colors = {
                    confirmed: "bg-emerald-500", pending: "bg-amber-500",
                    completed: "bg-blue-500", rejected: "bg-red-500",
                    canceled_by_user: "bg-slate-400", canceled_by_venue: "bg-slate-400",
                    cancelled_by_customer: "bg-slate-400", cancelled_by_venue: "bg-slate-400",
                    proposed: "bg-purple-500", change_proposed: "bg-purple-500",
                  };
                  return (
                    <div key={status}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">{t(`booking_status:${status}`, { defaultValue: status.replace(/_/g, " ") })}</span>
                        <span className="font-medium">{count} ({pct}%)</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div className={`${colors[status] || "bg-slate-400"} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Popular Venues */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h3 className="font-medium text-foreground mb-4">{t("admin_dashboard:popular_venues")}</h3>
              <div className="space-y-3">
                {(analytics.popular_venues || []).map((v, i) => (
                  <div key={v.venue_id} className="flex items-center gap-3" data-testid={`popular-venue-${i}`}>
                    <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{v.name}</p>
                      <p className="text-xs text-muted-foreground">{v.city_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{v.booking_count}</p>
                      <p className="text-xs text-muted-foreground">{v.total_guests} {t("common:guests")}</p>
                    </div>
                  </div>
                ))}
                {(analytics.popular_venues || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t("admin_dashboard:no_data_yet")}</p>}
              </div>
            </div>

            {/* Bookings by City */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h3 className="font-medium text-foreground mb-4">{t("admin_dashboard:bookings_by_city")}</h3>
              <div className="space-y-3">
                {Object.entries(analytics.bookings_by_city || {}).map(([city, count]) => {
                  const max = Math.max(...Object.values(analytics.bookings_by_city || {}), 1);
                  return (
                    <div key={city}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground capitalize">{city}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div className="bg-primary h-2 rounded-full" style={{ width: `${(count / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(analytics.bookings_by_city || {}).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t("admin_dashboard:no_data_yet")}</p>}
              </div>
            </div>
          </div>

          {/* Users by Role */}
          <div className="bg-card rounded-xl border border-border p-6 mb-6">
            <h3 className="font-medium text-foreground mb-4">{t("admin_dashboard:users_by_role")}</h3>
            <div className="flex flex-wrap gap-4">
              {Object.entries(analytics.role_breakdown || {}).map(([role, count]) => (
                <div key={role} className="bg-muted/40 rounded-lg px-4 py-3">
                  <p className="text-xs text-muted-foreground capitalize">{role.replace(/_/g, " ")}</p>
                  <p className="text-xl font-semibold">{count}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Login Activity */}
      <LoginActivityPanel />

      {/* Quick links */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Link to="/admin/venues" className="bg-card rounded-xl border border-border p-6 hover:border-primary transition-colors">
          <Building2 className="h-8 w-8 text-primary mb-4" />
          <h3 className="font-medium text-foreground">{t("admin_dashboard:manage_venues")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("admin_dashboard:manage_venues_desc")}</p>
        </Link>
        <Link to="/admin/taxonomy" className="bg-card rounded-xl border border-border p-6 hover:border-primary transition-colors">
          <Tag className="h-8 w-8 text-primary mb-4" />
          <h3 className="font-medium text-foreground">{t("admin_dashboard:manage_taxonomy")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("admin_dashboard:manage_taxonomy_desc")}</p>
        </Link>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon }) {
  return (
    <div className="bg-card rounded-xl border border-border p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="text-xl font-medium text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{title}</p>
        </div>
      </div>
    </div>
  );
}

function formatRelativeTime(iso, lang) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(lang === "el" ? "el" : "en", { numeric: "auto" });
    if (abs < 60) return rtf.format(diff, "second");
    if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
    if (abs < 2592000) return rtf.format(Math.round(diff / 86400), "day");
    return rtf.format(Math.round(diff / 2592000), "month");
  } catch {
    return new Date(iso).toLocaleString();
  }
}

function ProviderBadge({ provider }) {
  const { t } = useTranslation();
  const map = {
    google: { label: "Google", cls: "bg-blue-100 text-blue-700 border-blue-200", Icon: Globe },
    email: { label: t("admin_logins:provider_email"), cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: Mail },
  };
  const m = map[provider] || { label: provider || "—", cls: "bg-muted text-muted-foreground border-border", Icon: KeyRound };
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

function LoginActivityPanel() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [since, setSince] = useState("all"); // today | 7d | 30d | all

  // One-time stats fetch (stats are always 30-day/24h aggregates)
  useEffect(() => {
    axios
      .get(`${API}/admin/analytics/logins/stats`)
      .then((s) => setStats(s.data))
      .catch((err) => console.error("Login stats fetch failed:", err))
      .finally(() => setLoading(false));
  }, []);

  // Re-fetch the event list whenever the date filter changes
  useEffect(() => {
    setEventsLoading(true);
    axios
      .get(`${API}/admin/analytics/logins`, { params: { limit: 20, since } })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  }, [since]);

  if (loading) {
    return <div className="animate-pulse bg-muted h-60 rounded-xl mb-6" />;
  }
  if (!stats) return null;

  const providerTotal = Object.values(stats.by_provider || {}).reduce((a, b) => a + b, 0) || 1;
  const providerEntries = Object.entries(stats.by_provider || {});

  const filterOptions = [
    { value: "today", label: t("admin_logins:filter_today", { defaultValue: "Today" }) },
    { value: "7d", label: t("admin_logins:filter_7d", { defaultValue: "7 days" }) },
    { value: "30d", label: t("admin_logins:filter_30d", { defaultValue: "30 days" }) },
    { value: "all", label: t("admin_logins:filter_all", { defaultValue: "All" }) },
  ];

  return (
    <div className="mt-8" data-testid="admin-login-activity">
      <h2 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-primary" />
        {t("admin_logins:title")}
      </h2>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <LoginKpi label={t("admin_logins:signins_24h")} value={stats.successful.h24} icon={TrendingUp} tone="emerald" />
        <LoginKpi label={t("admin_logins:signins_7d")} value={stats.successful.d7} icon={TrendingUp} tone="emerald" />
        <LoginKpi label={t("admin_logins:signins_30d")} value={stats.successful.d30} icon={TrendingUp} tone="emerald" />
        <LoginKpi label={t("admin_logins:failed_24h")} value={stats.failed.h24} icon={AlertTriangle} tone="rose" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Provider split */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-medium text-foreground mb-4">{t("admin_logins:by_provider_30d")}</h3>
          <div className="space-y-3">
            {providerEntries.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">{t("admin_dashboard:no_data_yet")}</p>
            )}
            {providerEntries.map(([prov, count]) => {
              const pct = Math.round((count / providerTotal) * 100);
              const color = prov === "google" ? "bg-blue-500" : prov === "invitation" ? "bg-purple-500" : "bg-slate-500";
              return (
                <div key={prov}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <ProviderBadge provider={prov} />
                    <span className="font-medium">{count} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Top failures */}
          {(stats.top_failures || []).length > 0 && (
            <div className="mt-6 pt-4 border-t border-border">
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
                {t("admin_logins:top_failures_30d")}
              </h4>
              <ul className="space-y-1.5">
                {stats.top_failures.map((f) => (
                  <li key={f.reason} className="flex justify-between text-xs text-muted-foreground">
                    <span>{t(`admin_logins.reason.${f.reason}`, { defaultValue: f.reason.replace(/_/g, " ") })}</span>
                    <span className="font-medium text-foreground">{f.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Recent events table */}
        <div className="lg:col-span-2 bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h3 className="font-medium text-foreground">{t("admin_logins:recent_activity")}</h3>
            <div className="inline-flex items-center rounded-lg bg-muted/50 p-0.5 text-xs" data-testid="login-filter-segmented">
              {filterOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSince(opt.value)}
                  className={`px-3 py-1 rounded-md transition-colors ${
                    since === opt.value
                      ? "bg-background text-foreground shadow-sm font-medium"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`login-filter-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {eventsLoading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-12 bg-muted/40 rounded animate-pulse" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t("admin_dashboard:no_data_yet")}</p>
          ) : (
            <div
              className="overflow-y-auto overflow-x-auto -mx-2 pr-1 max-h-[480px] custom-scrollbar"
              data-testid="login-events-scroll-container"
            >
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card z-10">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-2 px-2 font-medium">{t("admin_logins:user")}</th>
                    <th className="py-2 px-2 font-medium">{t("admin_logins:provider")}</th>
                    <th className="py-2 px-2 font-medium">{t("admin_logins:status")}</th>
                    <th className="py-2 px-2 font-medium">{t("admin_logins:when")}</th>
                    <th className="py-2 px-2 font-medium">IP</th>
                  </tr>
                </thead>
                <tbody data-testid="admin-login-events-table">
                  {events.map((e) => (
                    <tr key={e.event_id} className="border-b border-border/50 last:border-0">
                      <td className="py-2.5 px-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {e.user_picture ? (
                            <img src={e.user_picture} alt="" className="w-6 h-6 rounded-full" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground">
                              {(e.user_name || e.email || "?").slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{e.user_name || e.email || "—"}</p>
                            {e.user_name && <p className="text-[11px] text-muted-foreground truncate">{e.email}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-2"><ProviderBadge provider={e.provider} /></td>
                      <td className="py-2.5 px-2">
                        {e.success ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium">● {t("admin_logins:success")}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-medium" title={e.failure_reason || ""}>● {t("admin_logins:failed")}</span>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-xs text-muted-foreground whitespace-nowrap" title={e.created_at}>
                        {formatRelativeTime(e.created_at, i18n.language)}
                      </td>
                      <td className="py-2.5 px-2 text-xs text-muted-foreground font-mono">{e.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LoginKpi({ label, value, icon: Icon, tone }) {
  const toneCls = tone === "rose"
    ? "text-rose-600 bg-rose-50"
    : tone === "emerald"
      ? "text-emerald-600 bg-emerald-50"
      : "text-primary bg-primary/10";
  return (
    <div className="bg-card rounded-xl border border-border p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${toneCls}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <p className="text-2xl font-semibold text-foreground">{(value ?? 0).toLocaleString()}</p>
    </div>
  );
}

function BookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const fmt = useFormatDate();

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      const { date_from, date_to } = getDateRange(dateFilter);
      if (date_from) params.set("date_from", date_from);
      if (date_to) params.set("date_to", date_to);
      const response = await axios.get(`${API}/admin/bookings?${params.toString()}`);
      setBookings(response.data.bookings || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, dateFilter]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/bookings/${deleteTarget.booking_id}`);
      toast.success(t("admin_bookings:toast_deleted"));
      setDeleteTarget(null);
      await fetchBookings();
    } catch {
      toast.error(t("admin_bookings:toast_delete_failed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          <Calendar className="h-6 w-6 text-primary" />
          {t("admin:bookings")} ({total})
        </h1>
      </div>
      <div className="bg-card rounded-xl border border-border mb-6 p-4 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("admin_bookings:search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="admin-bookings-search"
          />
        </div>
        <BookingDateFilter value={dateFilter} onChange={setDateFilter} />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="admin-booking-status-filter">
            <SelectValue placeholder={t("common:status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common:all")}</SelectItem>
            {["pending","confirmed","change_proposed","completed","rejected","canceled_by_user","canceled_by_venue","no_show"].map(s => (
              <SelectItem key={s} value={s}>{t(`booking_status:${s}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-16 rounded-lg" />)}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin_bookings:col_guest")}</TableHead>
                <TableHead>{t("admin:venues")}</TableHead>
                <TableHead>{t("common:date")}</TableHead>
                <TableHead>{t("common:time")}</TableHead>
                <TableHead>{t("common:guests")}</TableHead>
                <TableHead>{t("common:status")}</TableHead>
                <TableHead className="text-right">{t("admin_venues:col_actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">{t("common:no_results")}</TableCell>
                </TableRow>
              ) : bookings.map((booking) => (
                <TableRow key={booking.booking_id} data-testid={`admin-booking-row-${booking.booking_id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{booking.contact_name}</p>
                      <p className="text-xs text-muted-foreground">{booking.contact_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>{getLocalized(booking, "venue_name", lang) || booking.venue_name}</TableCell>
                  <TableCell>{fmt(booking.booking_date, "MMM d, yyyy")}</TableCell>
                  <TableCell>{booking.booking_time}</TableCell>
                  <TableCell>{booking.guest_count}</TableCell>
                  <TableCell><BookingStatusBadge status={booking.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(booking)}
                      title={t("admin_bookings:action_delete")}
                      data-testid={`delete-booking-${booking.booking_id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-booking-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin_bookings:delete_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin_bookings:delete_dialog_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("admin_venues:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-booking">
              {t("admin_venues:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EventsPage() {
  const { t } = useTranslation();
  return (
    <div data-testid="admin-events-page">
      <h1 className="text-2xl font-medium text-foreground mb-6 flex items-center gap-2">
        <CalendarDays className="h-6 w-6 text-primary" />
        {t("nav:events")}
      </h1>
      <div className="bg-card rounded-xl border border-border p-6 text-center py-12">
        <CalendarDays className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{t("admin_dashboard:events_coming_soon")}</p>
      </div>
    </div>
  );
}

function PermissionsPage() {
  const { t } = useTranslation();
  return (
    <div data-testid="admin-permissions-page">
      <h1 className="text-2xl font-medium text-foreground mb-6 flex items-center gap-2">
        <Shield className="h-6 w-6 text-primary" />
        {t("admin:settings")}
      </h1>
      <div className="bg-card rounded-xl border border-border p-6 text-center py-12">
        <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{t("admin_dashboard:permissions_coming_soon")}</p>
      </div>
    </div>
  );
}
